import React, {useMemo, useState} from 'react';
import {
  Alert,
  Box,
  Grid,
  TextField,
  Button,
  CircularProgress,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  GridValidRowModel,
  GridRenderCellParams,
  GridActionsCellItem,
  GridColDef,
} from '@mui/x-data-grid-pro';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {useField} from 'formik';
import {Panel} from '../../components/Panel/Panel';
import {themeColors} from '../../theme/Theme';
import {useDjangoContext} from '../../contexts/useAuthContext';
import {useAllStaff} from './useAllStaff';
import {StickyDataGrid} from '../../components/Common/StickyDataGrid';
import {useLocalStorage} from '../../hooks/useLocalStorage';
import {StaffDetails} from '../../schema-types/StaffDetails';
import {convertBooleanToCheckOrCross} from '../../utils/convertBooleanToCheckOrCross';
import HistoryDiffDialog from '../../components/AccountReviewDialog/HistoryDiffDialog';
import {HistoryQueryType} from '../../components/AccountReviewDialog/useHistoryDiff';
import {PermissionDialog} from '../../components/PermissionDialog/PermissionDialog';
import AutoField, {ActionType, SelectField} from '../../components/AutoForm/AutoFieldNew';
import withAutoFormNew from '../../components/AutoForm/AutoFormNew';
import {PermissionsFilterDialog} from '../../components/PermissionsFilterDialog/PermissionsFilterDialog';
import {usePermissionsAuditContext} from './PermissionsAuditProvider';
import {GetAllStaffVariables} from '../../schema-types/GetAllStaff';
import saltus from '../../shared/logger';

const classes = {
  item: {
    alignItems: 'center',
    minWidth: 200,
  },
};

const Admin: React.FC = () => {
  const {staffList, loading, staffError, getAllStaff} = useAllStaff();
  const {state, setState} = usePermissionsAuditContext();
  const {search, businessUnitIds, everything, permissionOverride, group, permission} =
    state;
  const {perms} = useDjangoContext();
  const [{value}, , {setValue}] = useField('daysSince');
  const [selectedStaff, setSelectedStaff] = useLocalStorage<StaffDetails | null>(
    'permissionAdminSelectedStaff',
    null
  );
  const [openDialog, setOpenDialog] = useState<boolean>(false);

  const [openHistoryDialog, setOpenHistoryDialog] = useState(false);
  const [selectedStaffId, setSelectedStaffId] = useState<string | null>(null);
  const handleDialogOpen = (id: string) => {
    setSelectedStaffId(id);
    setOpenHistoryDialog(true);
  };
  const handleDialogClose = () => {
    setSelectedStaffId(null);
    setOpenHistoryDialog(false);
  };

  const [openGroupHistoryDialog, setOpenGroupHistoryDialog] = useState(false);
  const [groupHistoryLoading, setGroupHistoryLoading] = useState(false);
  const handleGroupDialogOpen = () => {
    setGroupHistoryLoading(true);
    setOpenGroupHistoryDialog(true);
  };
  const handleGroupDialogClose = () => {
    setGroupHistoryLoading(false);
    setOpenGroupHistoryDialog(false);
  };
  const [openFilterDialog, setOpenFilterDialog] = useState(false);

  const handleCopyEmail = async (email: string) => {
    try {
      await navigator.clipboard.writeText(email);
      saltus.log('Email copied to clipboard');
    } catch (err) {
      saltus.error('Failed to copy: ', err);
    }
  };

  const handleOpenDialog = (staffMember: StaffDetails) => {
    setSelectedStaff(staffMember);
    setOpenDialog(true);
  };

  const emailColumnRenderCell = (params: GridRenderCellParams<GridValidRowModel>) => {
    const email = params.value || '';
    return (
      <Box sx={{display: 'flex', alignItems: 'center'}}>
        {email}
        {email !== '' && (
          <GridActionsCellItem
            icon={<ContentCopyIcon />}
            label="Copy"
            onClick={() => handleCopyEmail(email)}
            color="inherit"
          />
        )}
      </Box>
    );
  };

  const columns: readonly GridColDef<GridValidRowModel>[] = [
    {
      field: 'name',
      headerName: 'Staff Name',
      width: 175,
      renderCell(params) {
        return (
          <a
            href={`${window.location.origin}/staff/${params.id}`}
            target="_blank"
            style={{
              textDecoration: 'none',
              color: 'black',
              flex: 1,
            }}
            rel="noreferrer"
          >
            <Typography alignContent="center">
              <b>{params.value || ''}</b>
            </Typography>
          </a>
        );
      },
    },
    {
      field: 'email',
      headerName: 'Email',
      width: 225,
      renderCell: emailColumnRenderCell,
    },
    {
      field: 'businessUnit',
      headerName: 'Business Unit',
      width: 200,
      valueGetter: (_, row, column, apiRef) => {
        const this_row = row as StaffDetails;
        return `${this_row.businessUnit?.name}` || 'Not defined';
      },
    },
    {
      field: 'groups',
      headerName: 'Group',
      width: 100,
      valueGetter: (_, row, column, apiRef) => {
        const this_row = row as StaffDetails;
        return (
          `${this_row.groups?.[0]?.name.toLocaleUpperCase().replace('_', ' ')}` ||
          'Not defined'
        );
      },
    },
    {
      field: 'permissionOverride',
      headerName: 'Overriding Curo default groups/permissions?',
      width: 300,
      renderCell: (params) => {
        const row = params.row as StaffDetails;
        return convertBooleanToCheckOrCross(!!row.permissionOverride);
      },
    },
  ];

  const generateOptions = (count: number) => {
    return Array.from({length: count}, (_, index) => {
      const number = (index + 1).toString();
      return {label: number, value: number};
    });
  };

  const daysSinceField: SelectField = {
    type: ActionType.select,
    id: 'daysSince',
    label: 'Audit group changes in the last (days):',
    fullWidth: true,
    options: generateOptions(30),
    initialValue: '',
  };

  const variables: GetAllStaffVariables = {
    search,
    everything,
    businessUnitIds,
    permissionOverride,
    permission,
    group,
  };

  const filtersUsed = useMemo(() => {
    const stateKeysMinusSearch = Object.keys(state).filter((x) => x !== 'search');
    let result = 0;
    for (const key of stateKeysMinusSearch) {
      const val = (state as any)[key];
      if (val?.length >= 0) {
        result += Number(!!val.length);
      } else {
        result += Number(!!val);
      }
    }
    return result;
  }, [state]);

  if (staffError) {
    return <Alert severity="error">Error: {staffError.message}</Alert>;
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Panel title="All Users" color={themeColors.lightAlert}>
          <Box role="tabpanel" p={3}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                flex: 1,
              }}
            >
              <div style={classes.item}>
                <AutoField field={daysSinceField} disabled={loading} />
              </div>

              <div style={classes.item}>
                {value ? (
                  <Tooltip
                    title="Returns an audit report of all users whose groups have changed in the period specified."
                    arrow
                  >
                    <Button
                      variant="outlined"
                      onClick={handleGroupDialogOpen}
                      disabled={groupHistoryLoading}
                      startIcon={
                        groupHistoryLoading ? <CircularProgress size={20} /> : null
                      }
                    >
                      {groupHistoryLoading ? 'Loading...' : 'Run audit'}
                    </Button>
                  </Tooltip>
                ) : null}
              </div>
            </div>
            <Box display="flex" alignItems="baseline" justifyContent="end" gap="1rem">
              <TextField
                placeholder="Search by Staff Name"
                variant="outlined"
                fullWidth
                autoFocus
                margin="normal"
                value={search}
                onChange={(event) =>
                  setState((prevState) => ({...prevState, search: event.target.value}))
                }
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    // fire query on pressing enter key
                    getAllStaff({variables});
                  }
                }}
                inputProps={{
                  type: 'search',
                }}
                disabled={loading}
              />
              <Box width="7rem">
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => setOpenFilterDialog(true)}
                  disabled={loading}
                >
                  Filters ({filtersUsed})
                </Button>
              </Box>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  getAllStaff({variables});
                }}
                disabled={loading}
              >
                Search
              </Button>
            </Box>

            <StickyDataGrid
              rows={staffList}
              columns={columns}
              rowHeight={60}
              loading={loading}
              onRowDoubleClick={(row) => {
                window.open(`${window.location.origin}/staff/${row.id}`);
              }}
            />
          </Box>
          <PermissionDialog
            open={openDialog}
            selectedStaff={selectedStaff}
            onClose={() => setOpenDialog(false)}
          />
          <PermissionsFilterDialog
            open={openFilterDialog}
            onClose={() => setOpenFilterDialog(false)}
            formFields={[]}
          />
          <HistoryDiffDialog
            isOpen={openHistoryDialog}
            handleClose={handleDialogClose}
            staffId={selectedStaffId}
            queryType={HistoryQueryType.STAFF}
          />
          <HistoryDiffDialog
            isOpen={openGroupHistoryDialog}
            handleClose={handleGroupDialogClose}
            queryType={HistoryQueryType.GROUP}
            daysSince={value}
            setGroupHistoryLoading={setGroupHistoryLoading}
          />
        </Panel>
      </Grid>
    </Grid>
  );
};

export const AdminPage = withAutoFormNew(Admin, undefined, {
  debugPosition: 'bottom',
});
